/**
 * SAP Ship Doc — the intermediate steps, between the master sheet and the finished document.
 *
 * Max, 2026-09-13: "hiện chúng ta làm thẳng ra bước cuối cùng nên khi làm sẽ cực, và Quang cũng ko đặt
 * trọn niềm tin nên cần bạn thiết kế thêm các phần trung gian trước phần output cuối."
 *
 * WHAT WAS WRONG. The screen went straight from the master sheet to the finished grid and handed the
 * reader 527 rows marked "Seq to confirm" — a flag, with no evidence attached. To decide any one of
 * them he had to open SAP and redo the lookup by hand, which is the work this module exists to remove.
 * And there was nowhere to record that he HAD decided: "not flagged" and "somebody checked it" were
 * the same column, so nobody could tell how much of the quarter had actually been gone through.
 *
 * SO EACH STEP ANSWERS THREE QUESTIONS, in this order, for every row:
 *   which line did you pick · why that one · what else was on the table.
 * The first two come from the row itself (the sync writes the reason down beside the answer), so a
 * list of 527 costs one request. The third is fetched when the reader opens a row, because it is the
 * only part that needs SAP's other candidates.
 *
 * CONFIRMING DOES NOT CHANGE THE DOCUMENT. That is the point: the common case is "the lookup was
 * right", and it has to be one press. Typing a different seq is the same press with a line picked.
 *
 * NOT A GATE — Max, 2026-09-13: the export stays available with the steps unfinished, and says what is
 * unfinished instead of refusing. The person doing this work is the one under time pressure.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, AlertTriangle, RotateCcw, Loader } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import i18n from './i18n';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import { BACKEND_URL } from './backendConfig';
import useFillHeight from './useFillHeight';

const API = `${BACKEND_URL}/api/production/sap-ship-doc`;
const n0 = (v) => (v === null || v === undefined || Number.isNaN(v) ? '—' : Number(v).toLocaleString('en-US'));

/* WHAT COUNTS AS WORK, in one place. Every count on the step bar, every list and the warning on the
   export are read from these three, so the bar can never disagree with the list under it. */
export const needsSfp = (l) => l.sfpSeqAmbiguous && !l.sfpSeqConfirmedAt;
export const needsItem = (l) => (l.itemSeqAmbiguous || !l.itemPo) && !l.itemSeqConfirmedAt;
export const qtyKey = (l) => `${l.sfpPo}|${l.sfpSeqSap}`;

/**
 * The guide's pivot: every row drawing on one SFP line must add back up to that line's PO quantity.
 *
 * EVERY ROW OF THE SFP LINE, including the ones whose ITEM line has not been found yet. This started
 * out skipping them — which made the check LENIENT in exactly the wrong direction: a group missing one
 * of its destinations looked short and was still counted as balanced if the rest happened to fit.
 * Measured on Q3 2026, skipping them showed 157 groups off where Quang's own pivot finds 248, and 105
 * groups held at least one row with no ITEM line. His check runs straight after the SFP lookup —
 * "lấy số lượng thực tế của PO trừ cho số lượng của mình tìm ra" — and at that point in his routine no
 * ITEM number is filled in at all, so it cannot depend on one.
 */
export const qtyGroups = (lines) => {
  const map = new Map();
  lines.forEach((l) => {
    if (!l.sfpPo) return;
    const k = qtyKey(l);
    if (!map.has(k)) {
      map.set(k, { key: k, sfpPo: l.sfpPo, sfpSeq: l.sfpSeqSap, sfpCode: l.sfpCode, sfpQty: Number(l.sfpQty) || 0, rows: [] });
    }
    map.get(k).rows.push(l);
  });
  return [...map.values()].map((g) => {
    const sum = g.rows.reduce((a, r) => a + (Number(r.qty) || 0), 0);
    /* WHY A GROUP IS OFF, measured on Q3 2026: of 157 SFP lines whose split does not add up, 152 are
       off by EXACTLY the PO quantity and 153 have two document rows pointing at ONE ITEM line. It is
       not 157 problems, it is one problem 153 times — two destinations sharing an ITEM line because
       SAP holds only one. Saying so on the row is the difference between a worklist and a puzzle. */
    const withItem = g.rows.filter((r) => r.itemPo);
    const lines = new Set(withItem.map((r) => `${r.itemPo}|${r.itemSeq}`));
    const shared = withItem.length > lines.size ? [...lines][0] : null;
    const noItem = g.rows.length - withItem.length;
    return {
      ...g,
      sum,
      diff: Math.round((sum - g.sfpQty) * 1000) / 1000,
      itemLines: lines.size,
      shared,
      noItem,
      checked: g.rows.every((r) => r.qtyCheckedAt),
    };
  });
};

/* How doubtful a proposal is, so the worst rows are met first rather than found on page nine.
   Read from the reason the server stored, never recomputed here — a second opinion about the same
   row is exactly what destroys the reader's trust. */
const severity = (l, which) => {
  const w = (which === 'sfp' ? l.sfpSeqWhy : l.itemSeqWhy) || {};
  if (which === 'sfp') {
    if (w.none) return 100;
    let s = 0;
    if (w.vendorMatched === false) s += 40;
    if (w.exactDate === false) s += Math.min(30, Math.round((w.dayGap || 0) / 15));
    if ((w.tied || 0) > 1) s += 10 + (w.tied || 0);
    if (w.tranche) s += 8;
    return s;
  }
  if (w.fromSo) return 0;
  if (w.noPoOnSo) return 85;
  if (w.none) return 100;
  if (w.outOfReach) return 90;
  if (w.noSfp) return 95;
  let s = 0;
  if (w.allTaken) s += 30;
  if ((w.sameQty || 0) === 0) s += 25;
  if ((w.inReach || 0) > 1) s += Math.min(20, w.inReach);
  s += Math.min(15, Math.round((w.dayGap || 0) / 15));
  return s;
};

/** The stored reason, rendered as the chips a person reads. Never a sentence from the database. */
function Why({ why, which }) {
  const { t } = useTranslation();
  if (!why) return <span className="ssd-why-none">{t('xShipDoc.whyNone')}</span>;
  const chips = [];
  // `text`, not `t`: `t` is the translator here.
  const bad = (text) => chips.push({ t: text, tone: 'bad' });
  const ok = (text) => chips.push({ t: text, tone: 'ok' });
  const flat = (text) => chips.push({ t: text, tone: '' });
  if (which === 'sfp') {
    if (why.none) return <span className="ssd-chip bad">{t('xShipDoc.whySfpNone', { code: why.code })}</span>;
    if (why.vendorMatched) ok(t('xShipDoc.whyVendorMatch', { po: why.vendorFromPo }));
    else bad(t('xShipDoc.whyVendorNo'));
    if (why.exactDate) ok(t('xShipDoc.whyExfExact'));
    else bad(t('xShipDoc.whyNearestDate', { count: Number(why.dayGap) || 0, n: n0(why.dayGap) }));
    if ((why.tied || 0) > 1) bad(t('xShipDoc.whyTied', { n: why.tied }));
    if (why.tranche) bad(t('xShipDoc.whyTranche'));
    flat(t('xShipDoc.whyCandidates', { count: Number(why.considered) || 0, n: n0(why.considered) }));
  } else {
    /* Since 2026-10-14 a sales-order row's ITEM line is read from SAP, not looked up — and a row with
       no PO behind it is left without one rather than lent another order's line. */
    if (why.fromSo) {
      return (
        <span className="ssd-chip ok">
          {why.salesOrder
            ? t('xShipDoc.whyFromSo', { so: `${why.salesOrder}${why.soSeq ? `/${why.soSeq}` : ''}`, po: why.po, seq: why.seq })
            : t('xShipDoc.whyFromSoBare', { po: why.po, seq: why.seq })}
        </span>
      );
    }
    if (why.noPoOnSo) {
      return (
        <span className="ssd-chip bad">
          {why.confirmNo ? t('xShipDoc.whyNoPoConfirm', { no: why.confirmNo }) : t('xShipDoc.whyNoPoLine')}
        </span>
      );
    }
    if (why.noSfp) return <span className="ssd-chip bad">{t('xShipDoc.whyNoSfp')}</span>;
    if (why.none) return <span className="ssd-chip bad">{t('xShipDoc.whyItemNone', { code: why.code })}</span>;
    if (why.outOfReach) {
      return (
        <span className="ssd-chip bad">
          {t('xShipDoc.whyOutOfReach', { reach: why.reach, po: why.fromPo, n: n0(why.considered) })}
        </span>
      );
    }
    if (why.soLineMissing) bad(t('xShipDoc.whySoLineMissing', { line: why.soLineMissing }));
    if ((why.sameQty || 0) > 0) ok(t('xShipDoc.whySameQty', { count: why.sameQty, n: why.sameQty }));
    else bad(t('xShipDoc.whyNoQty', { n: n0(why.wantedQty) }));
    if (why.allTaken) bad(t('xShipDoc.whyAllTaken'));
    else if ((why.free || 0) > 1) flat(t('xShipDoc.whyFree', { n: why.free }));
    if ((why.inReach || 0) > 1) flat(t('xShipDoc.whyInReach', { n: why.inReach, po: why.fromPo }));
    if (why.dayGap) flat(t('xShipDoc.whyDaysFromExf', { count: Number(why.dayGap) || 0, n: n0(why.dayGap) }));
  }
  return (
    <>
      {chips.map((c) => <span key={c.t} className={`ssd-chip ${c.tone}`}>{c.t}</span>)}
    </>
  );
}

/** The candidate lines, fetched when a row is opened. Picking one types the seq and confirms in one press. */
function Candidates({ line, which, onConfirm, busy }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [picked, setPicked] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/${line.id}/candidates`, { credentials: 'include' });
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || i18n.t('xShipDoc.failed', { status: res.status }));
        if (alive) setData(d);
      } catch (e) { if (alive) setErr(e.message); }
    })();
    return () => { alive = false; };
  }, [line.id]);

  if (err) return <div className="sp-error"><AlertTriangle size={13} /> {err}</div>;
  if (!data) return <div className="ssd-loading"><Loader size={13} /> {t('xShipDoc.readingSapLower')}</div>;

  const side = which === 'sfp' ? data.sfp : data.item;
  const current = which === 'sfp' ? data.current.sfpSeq : data.current.itemSeq;
  const all = side.candidates || [];
  if (!all.length) return <div className="ssd-loading">{t('xShipDoc.candNone')}</div>;
  /* A shortlist, not the archive. One SFP code can sit on 22 PO lines going back three years and the
     twentieth is never the answer — they are already sorted with the likeliest first. */
  const rows = showAll ? all : all.slice(0, 8);

  return (
    <div className="ssd-cands">
      <table>
        <thead>
          <tr>
            <th />
            <th>PO</th><th>{t('xShipDoc.colSeq')}</th><th>{t('xShipDoc.colDelivery')}</th><th className="num">{t('xShipDoc.colQty')}</th>
            <th>{t('xShipDoc.colSupplier')}</th><th>{t('xShipDoc.colNotes')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const id = `${c.po}|${c.seq}`;
            const isCurrent = String(c.seq) === String(current)
              && String(c.po) === String(which === 'sfp' ? line.sfpPo : line.itemPo);
            return (
              <tr key={id} className={`${c.chosen ? 'proposed' : ''} ${isCurrent ? 'current' : ''}`}>
                <td>
                  <input
                    type="radio"
                    name={`cand-${line.id}`}
                    checked={picked === id || (!picked && isCurrent)}
                    onChange={() => setPicked(id)}
                  />
                </td>
                <td className="mono">{c.po}</td>
                <td className="mono">{c.seq}</td>
                <td>{c.date || '—'}</td>
                <td className="num">{n0(c.qty)}</td>
                <td className="ssd-sup">{c.supplier}</td>
                <td className="ssd-cnotes">
                  {isCurrent && <span className="ssd-chip ok">{t('xShipDoc.candOnDoc')}</span>}
                  {c.chosen && !isCurrent && <span className="ssd-chip">{t('xShipDoc.candPick')}</span>}
                  {which === 'sfp' && c.exactDate && <span className="ssd-chip ok">{t('xShipDoc.candDateExact')}</span>}
                  {which === 'sfp' && !c.exactDate && <span className="ssd-chip">{t('xShipDoc.candDaysOff', { n: n0(c.dayGap) })}</span>}
                  {which === 'sfp' && !c.sameVendor && <span className="ssd-chip bad">{t('xShipDoc.candOtherVendor')}</span>}
                  {which === 'item' && c.sameQty && <span className="ssd-chip ok">{t('xShipDoc.candSameQty')}</span>}
                  {which === 'item' && c.taken && (
                    <span className="ssd-chip bad">
                      {c.takenBy
                        ? t('xShipDoc.candUsedBy', { who: c.takenBy.destination || c.takenBy.rowKey })
                        : t('xShipDoc.candUsedByOther')}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="ssd-cands-foot">
        {all.length > rows.length && (
          <button type="button" className="sp-btn" onClick={() => setShowAll(true)}>
            {t('xShipDoc.showOther', { n: all.length - rows.length })}
          </button>
        )}
        <button
          type="button"
          className="sp-btn primary"
          disabled={busy || !picked}
          onClick={() => onConfirm(line, picked.split('|')[1])}
        >
          <Check size={13} /> {t('xShipDoc.useLine')}
        </button>
      </div>
    </div>
  );
}

/**
 * Step 2 and step 3. One component: the shape of the decision is the same — a proposal, a reason and a
 * list of lines — and two copies would drift the first time one of them was improved.
 */
export function SeqStep({ which, lines, canEdit, onLines, note }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState({});        // rowId -> candidates open
  const [openPo, setOpenPo] = useState({});    // po -> group open
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [done, setDone] = useState(false);     // show the confirmed rows too

  const needs = which === 'sfp' ? needsSfp : needsItem;
  const confirmedAt = (l) => (which === 'sfp' ? l.sfpSeqConfirmedAt : l.itemSeqConfirmedAt);
  const confirmedBy = (l) => (which === 'sfp' ? l.sfpSeqConfirmedBy : l.itemSeqConfirmedBy);
  const seqOf = (l) => (which === 'sfp' ? l.sfpSeq : l.itemSeq);
  const poOf = (l) => (which === 'sfp' ? l.sfpPo : l.itemPo);

  /* GROUPED BY CUSTOMER PO — Max, 2026-09-21: "xét trên customer PO chứ ko phải PO / vendor PO".
     It was grouped by the vendor PO the lookup had chosen, which is the system's own key, not the
     thing being shipped to somebody: one customer's order is split across several vendor POs, so a
     person working through an order had to find its rows in three or four separate blocks.

     This changes only how the rows are GATHERED on screen. What the lookup decides, and the flag on
     each row, are untouched — the vendor PO is still printed on every row, where it is a fact about
     that row rather than the shape of the page.

     A row with no customer PO is its own block rather than being folded into a neighbour's: half
     the rows have none, and a bucket that pretended they belonged somewhere would be the screen
     inventing a grouping that the data does not have.
     The block of rows with none is keyed '' and named where it is drawn, in the reader's language. */
  const groupKeyOf = (l) => String(l.customerPo || '').trim();
  const groups = useMemo(() => {
    const pool = lines.filter((l) => (done ? confirmedAt(l) : needs(l)));
    const map = new Map();
    pool.forEach((l) => {
      const key = groupKeyOf(l);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    });
    return [...map.entries()]
      .map(([po, rows]) => ({
        po,
        // The vendor POs behind this customer's rows, so the block still says which orders it spans.
        pos: [...new Set(rows.map((r) => poOf(r)).filter(Boolean))],
        rows: [...rows].sort((a, b) => severity(b, which) - severity(a, which)),
        worst: Math.max(...rows.map((r) => severity(r, which))),
      }))
      .sort((a, b) => b.worst - a.worst || b.rows.length - a.rows.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, which, done]);

  const total = groups.reduce((a, g) => a + g.rows.length, 0);
  const shown = showAll ? groups : groups.slice(0, 12);

  const send = useCallback(async (body) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/confirm`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: which, ...body }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xShipDoc.failed', { status: res.status }));
      onLines(d.lines || []);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [which, onLines]);

  const confirmRow = (l, seq) => send(seq === undefined ? { ids: [l.id] } : { id: l.id, seq });
  const confirmGroup = (g) => send({ ids: g.rows.map((r) => r.id) });
  const undo = (l) => send({ ids: [l.id], clear: true });

  if (!total) {
    return (
      <div className="ssd-step-done">
        <Check size={16} />
        {done ? t('xShipDoc.seqNoneConfirmed') : t('xShipDoc.seqNothingLeft')}
        <button type="button" className="sp-btn" onClick={() => setDone(!done)}>
          {done ? t('xShipDoc.backToLeft') : t('xShipDoc.showConfirmed')}
        </button>
      </div>
    );
  }

  return (
    <div className="ssd-step">
      <div className="ssd-step-head">
        <strong>{total}</strong>
        <span>
          {t(done ? 'xShipDoc.seqHeadConfirmed' : 'xShipDoc.seqHeadDecide', { count: groups.length, n: groups.length })}
        </span>
        {!!note && <em>{note}</em>}
        <button type="button" className="sp-btn" onClick={() => setDone(!done)}>
          {done ? t('xShipDoc.whatLeft') : t('xShipDoc.whatConfirmed')}
        </button>
      </div>
      {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}

      {shown.map((g) => {
        const isOpen = openPo[g.po] !== false;
        return (
          <div className="ssd-group" key={g.po}>
            <div className="ssd-group-head">
              <button type="button" className="ssd-group-toggle"
                onClick={() => setOpenPo((p) => ({ ...p, [g.po]: !isOpen }))}>
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="mono">{g.po || t('xShipDoc.noCustomerPo')}</span>
                <em>{t('xShipDoc.rows', { count: g.rows.length, n: g.rows.length })}</em>
                {/* The vendor POs underneath: the block is one customer's order, and which
                    purchase orders it is spread over is the next thing somebody asks. */}
                {g.pos.length > 0 && (
                  <em className="ssd-group-pos">
                    {g.pos.length === 1 ? `PO ${g.pos[0]}` : t('xShipDoc.nPos', { n: g.pos.length })}
                  </em>
                )}
              </button>
              {canEdit && !done && (
                <button type="button" className="sp-btn" disabled={busy} onClick={() => confirmGroup(g)}>
                  <Check size={13} /> {t('xShipDoc.confirmAll', { n: g.rows.length })}
                </button>
              )}
            </div>

            {isOpen && g.rows.map((l) => (
              <div className={`ssd-case ${confirmedAt(l) ? 'is-done' : ''}`} key={l.id}>
                <div className="ssd-case-top">
                  <button type="button" className="ssd-case-toggle"
                    onClick={() => setOpen((o) => ({ ...o, [l.id]: !o[l.id] }))}>
                    {open[l.id] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  </button>
                  <span className="ssd-case-dest">{l.destination || '—'}</span>
                  <span className="ssd-case-po">{l.customerPo || ''}</span>
                  <span className="ssd-case-desc">{l.description} {l.color ? `· ${l.color}` : ''}</span>
                  <span className="ssd-case-qty">{t('xShipDoc.pcs', { n: n0(l.qty) })}</span>
                  <span className="ssd-case-exf">EXF {l.exfDate || '—'}</span>
                  <span className="ssd-case-seq">
                    {which === 'sfp' ? t('xShipDoc.colSfpSeq') : t('xShipDoc.colItemSeq')}
                    <strong>{seqOf(l) || '—'}</strong>
                    {!!(which === 'sfp' ? l.sfpSeqManual : l.itemSeqManual) && <em>{t('xShipDoc.typed')}</em>}
                  </span>
                  {confirmedAt(l) ? (
                    <span className="ssd-case-ok">
                      <Check size={13} /> {confirmedBy(l)}
                      {canEdit && (
                        <button type="button" className="ssd-undo" title={t('xShipDoc.undoConfirm')}
                          disabled={busy} onClick={() => undo(l)}>
                          <RotateCcw size={12} />
                        </button>
                      )}
                    </span>
                  ) : canEdit && (
                    <button type="button" className="sp-btn primary" disabled={busy}
                      onClick={() => confirmRow(l)}>
                      <Check size={13} /> {t('xShipDoc.confirm')}
                    </button>
                  )}
                </div>
                <div className="ssd-case-why"><Why why={which === 'sfp' ? l.sfpSeqWhy : l.itemSeqWhy} which={which} /></div>
                {open[l.id] && (
                  <Candidates line={l} which={which} busy={busy}
                    onConfirm={(row, seq) => confirmRow(row, seq)} />
                )}
              </div>
            ))}
          </div>
        );
      })}

      {groups.length > shown.length && (
        <button type="button" className="sp-btn ssd-more" onClick={() => setShowAll(true)}>
          {t('xShipDoc.showOtherCpo', { count: groups.length - shown.length, n: groups.length - shown.length })}
        </button>
      )}
    </div>
  );
}

/**
 * Step 4 — the guide's pivot, as a worklist instead of a filter.
 *
 * "lấy số lượng thực tế của PO trừ cho số lượng của mình tìm ra — nếu nó bằng 0 thì nó khớp". The old
 * screen could only mark the rows; what a person needs is the GROUP: the SFP line, its PO quantity,
 * and the rows that draw on it side by side, so the missing piece is visible rather than hunted.
 */
export function QtyStep({ lines, canEdit, onLines }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState({});
  const [showBalanced, setShowBalanced] = useState(false);
  const qtyBox = useRef(null);

  const groups = useMemo(() => {
    const all = qtyGroups(lines);
    const work = all.filter((g) => (showBalanced ? Math.abs(g.diff) < 0.001 : Math.abs(g.diff) >= 0.001));
    return work.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  }, [lines, showBalanced]);
  useFillHeight(qtyBox, [groups.length > 0, !!err]);

  const send = async (ids, clear) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/confirm`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: 'qty', ids, clear }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xShipDoc.failed', { status: res.status }));
      onLines(d.lines || []);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!groups.length) {
    return (
      <div className="ssd-step-done">
        <Check size={16} />
        {showBalanced ? t('xShipDoc.qtyNoneBalance') : t('xShipDoc.qtyAllBalance')}
        <button type="button" className="sp-btn" onClick={() => setShowBalanced(!showBalanced)}>
          {showBalanced ? t('xShipDoc.backToOff') : t('xShipDoc.showBalanced')}
        </button>
      </div>
    );
  }

  return (
    <div className="ssd-step">
      <div className="ssd-step-head">
        <strong>{groups.length}</strong>
        <span>
          {t(showBalanced ? 'xShipDoc.qtyHeadBalance' : 'xShipDoc.qtyHeadOff', { count: groups.length })}
        </span>
        <button type="button" className="sp-btn" onClick={() => setShowBalanced(!showBalanced)}>
          {showBalanced ? t('xShipDoc.whatOff') : t('xShipDoc.whatBalances')}
        </button>
      </div>
      {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}

      {/* A box of its own, as step 1: 248 groups is a page nobody can hold in their head. */}
      <div className="ssd-src-scroll" ref={qtyBox}>
        <table className="ssd-qty">
        <thead>
          <tr>
            <th />
            <th>{t('xShipDoc.colSfpPo')}</th><th>{t('xShipDoc.colSeq')}</th><th>{t('xShipDoc.colSfpCode')}</th>
            <th className="num">{t('xShipDoc.colPoQty')}</th><th className="num">{t('xShipDoc.colSplitTotal')}</th><th className="num">{t('xShipDoc.colGap')}</th>
            <th className="num">{t('xShipDoc.colRows')}</th><th>{t('xShipDoc.colWhyChecked')}</th><th />
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              <tr className={Math.abs(g.diff) >= 0.001 ? 'off' : ''}>
                <td>
                  <button type="button" className="ssd-group-toggle"
                    onClick={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}>
                    {open[g.key] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  </button>
                </td>
                <td className="mono">{g.sfpPo}</td>
                <td className="mono">{g.sfpSeq}</td>
                <td className="mono">{g.sfpCode}</td>
                <td className="num">{n0(g.sfpQty)}</td>
                <td className="num">{n0(g.sum)}</td>
                <td className={`num ${Math.abs(g.diff) >= 0.001 ? 'ssd-gap' : ''}`}>
                  {g.diff > 0 ? `+${n0(g.diff)}` : n0(g.diff)}
                </td>
                <td className="num">{g.rows.length}</td>
                <td>
                  {g.shared && (
                    <span className="ssd-chip bad" title={t('xShipDoc.sharedHint')}>
                      {t('xShipDoc.sharedItem', {
                        n: g.rows.length - g.itemLines + 1, po: g.shared.split('|')[0], seq: g.shared.split('|')[1],
                      })}
                    </span>
                  )}
                  {!!g.noItem && (
                    <span className="ssd-chip" title={t('xShipDoc.noItemHint')}>
                      {t('xShipDoc.noItemRows', { count: g.noItem, n: g.noItem })}
                    </span>
                  )}
                  {g.checked && <span className="ssd-chip ok">{g.rows[0].qtyCheckedBy}</span>}
                </td>
                <td>
                  {canEdit && (g.checked
                    ? (
                      <button type="button" className="sp-btn tiny" disabled={busy}
                        onClick={() => send(g.rows.map((r) => r.id), true)}>
                        <RotateCcw size={12} />
                      </button>
                    )
                    : (
                      <button type="button" className="sp-btn" disabled={busy}
                        onClick={() => send(g.rows.map((r) => r.id), false)}>
                        <Check size={13} /> {t('xShipDoc.checked')}
                      </button>
                    ))}
                </td>
              </tr>
              {open[g.key] && g.rows.map((r) => (
                <tr className="ssd-qty-row" key={r.id}>
                  <td />
                  <td colSpan={3}>{r.destination} · {r.customerPo || '—'}</td>
                  <td />
                  <td className="num">{n0(r.qty)}</td>
                  <td colSpan={2}>{r.description} {r.color ? `· ${r.color}` : ''}</td>
                  <td colSpan={2}>{t('xShipDoc.itemSeqLine', { po: r.itemPo || '—', seq: r.itemSeq || '—' })}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
}


/**
 * A table with the portal's own Excel column menus and a fixed 15-row window — Max, 2026-09-13:
 * "cho filter như excel và chỉ hiện 15 dòng có thể scroll down xuống để xem".
 *
 * It reuses SortTh and the three helpers from the Sample Tracker rather than growing a second filter
 * implementation: the funnel, the search box inside it, the date tree and the "(Blanks)" handling are
 * all things this screen would otherwise get subtly differently.
 *
 * The header is two sticky rows (a group band, then the labels) with heights fixed in CSS, because a
 * sticky row's `top` has to be a number and measuring it on every scroll is how the frozen columns
 * elsewhere in this codebase ended up 1px out. And the table is `border-collapse: separate`: with
 * `collapse` the rows scroll THROUGH the shared border lane of a sticky header, which has cost this
 * codebase an afternoon before.
 */
function FilterTable({ columns, rows, bands, getRowKey, getRowClass, empty }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);

  const getters = useMemo(() => Object.fromEntries(columns.map((c) => [
    c.key, c.num ? (r) => Number(r[c.key] ?? 0) : (r) => (r[c.key] ?? ''),
  ])), [columns]);

  const shown = useMemo(() => {
    const afterCols = Object.keys(colFilters).length
      ? rows.filter((r) => rowPassesColumnFilters(r, colFilters, getters)) : rows;
    return sortRows(afterCols, sort, getters);
  }, [rows, colFilters, sort, getters]);

  const thProps = (key) => ({
    sortKey: key,
    sort,
    onSort: (k, dir) => setSort((p) => (dir ? { key: k, dir }
      : (p.key === k ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' }))),
    filterValues: openFilter?.key === key ? distinctColumnValues(rows, key, colFilters, getters) : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => setOpenFilter((p) => (p && p.key === k ? null : { key: k, anchor })),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, values, allValues) => {
      setColFilters((prev) => {
        const next = { ...prev };
        const isAll = allValues && allValues.length && values.length >= allValues.length;
        if (!values.length || isAll) delete next[k]; else next[k] = values;
        return next;
      });
      setOpenFilter(null);
    },
  });

  const filteredCount = Object.keys(colFilters).length;
  const box = useRef(null);
  useFillHeight(box, [filteredCount > 0]);
  return (
    <>
      {/* A count of its own only while the column funnels narrow the list (Max, 2026-10-20: the bare
          "721 rows" under "Rows in · 721" said the tab's number twice). */}
      {!!filteredCount && (
        <div className="ssd-src-count">
          {t('xShipDoc.ftCount', { count: rows.length, shown: n0(shown.length), n: n0(rows.length) })}
          <button type="button" className="sp-btn tiny" onClick={() => { setColFilters({}); setOpenFilter(null); }}>
            {t('xShipDoc.ftClear', { count: filteredCount, n: filteredCount })}
          </button>
        </div>
      )}
      <div className="ssd-src-scroll" ref={box}>
        {/* Declared widths through a colgroup, with the table LAID OUT FIXED: on an auto layout the
            browser gives the funnel button its own share of the header cell and the label loses the
            room — "Customer PO#" came out as "Customer P…" while the data column beside it had space
            to spare. The sum is the table's min-width, so the box scrolls sideways rather than
            squeezing every column. */}
        <table className="ssd-src" style={{ minWidth: columns.reduce((a, c) => a + (c.w || 110), 0) }}>
          <colgroup>
            {columns.map((c) => <col key={c.key} style={{ width: c.w || 110 }} />)}
          </colgroup>
          <thead>
            {!!bands && (
              <tr className="ssd-src-group">
                {bands.map((b, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <th key={i} colSpan={b.span} className={b.cls || ''}>{b.label}</th>
                ))}
              </tr>
            )}
            <tr className="ssd-src-labels">
              {columns.map((c) => (
                <SortTh key={c.key} label={c.label} className={c.num ? 'num' : ''} {...thProps(c.key)} />
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={getRowKey(r)} className={getRowClass ? getRowClass(r) : ''}>
                {columns.map((c) => (
                  <td key={c.key} className={`${c.num ? 'num' : ''} ${c.cls ? c.cls(r) : ''}`}>
                    {c.render ? c.render(r) : (r[c.key] === '' || r[c.key] === null || r[c.key] === undefined
                      ? '—' : (c.num ? n0(r[c.key]) : r[c.key]))}
                  </td>
                ))}
              </tr>
            ))}
            {!shown.length && (
              <tr><td colSpan={columns.length} className="ssd-src-empty">{empty || t('xShipDoc.ftEmpty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * STEP 1 — the source, and what SAP says about it.
 *
 * Max, 2026-09-13: "tôi cần luôn bước 1 nguồn vì tôi cũng muốn Quang double check và các thông tin
 * được lấy từ SAP và anh ấy sẽ so sánh."
 *
 * The guide starts by copying three months out of the Master Sheet and downloading ZMMR00100 to look
 * the sequence numbers up. That comparison happens in two windows with two files; here it is one row
 * with a line down the middle — the sheet's figures on the left, the PO line the portal matched them
 * to on the right, and a mark where they disagree.
 *
 * THE TWO LISTS BESIDE IT ARE THE POINT. "Left out" is every master row the builder dropped and why:
 * a row that vanishes silently is the one nobody can check, and there are 151 of them in Q3 2026.
 * "Not used" reads the other way — PO lines SAP holds in this window that no row points at — and is
 * the only thing here that can catch a row the master sheet is MISSING.
 */
export function SourceStep({ from, to, readAgain = 0 }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('in');
  /* Read again is the page's own reload button (2026-10-20): two refresh icons a row apart did the
     same-looking thing to two different things. */
  const fresh = readAgain;

  useEffect(() => {
    let alive = true;
    setData(null); setErr('');
    (async () => {
      try {
        const res = await fetch(`${API}/source?from=${from}&to=${to}${fresh ? '&fresh=1' : ''}`,
          { credentials: 'include' });
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || i18n.t('xShipDoc.failed', { status: res.status }));
        if (alive) setData(d);
      } catch (e) { if (alive) setErr(e.message); }
    })();
    return () => { alive = false; };
  }, [from, to, fresh]);

  if (err) return <div className="sp-error"><AlertTriangle size={14} /> {err}</div>;
  if (!data) {
    return (
      <div className="ssd-loading">
        <Loader size={14} /> {t('xShipDoc.srcLoading')}
      </div>
    );
  }

  const s = data.summary;
  /* FLAT ROWS, because the column filters and the sort read `row[key]` — a nested master/sap shape
     would need its own getter for every column and the filter menu would have nothing to list. */
  const kept = tab === 'diff' ? data.rows.filter((r) => r.diffs.some((d) => d === 'date' || d === 'price'
    || d === 'noSfpLine' || d === 'noItemLine')) : data.rows;
  const flatRows = kept.map((r) => ({
    rowKey: r.rowKey,
    destination: r.destination,
    customerPo: r.customerPo,
    sfpCode: r.sfpCode,
    description: `${r.description}${r.color ? ` · ${r.color}` : ''}`,
    mQty: r.master.qty,
    mExf: r.master.exfDate || '',
    mPrice: r.master.unitPrice,
    mHnxPo: r.master.hnxPo,
    sSfpPo: r.sap.sfpPo,
    sSeq: r.sap.sfpSeq,
    sPoQty: r.sap.poQty,
    sDelivery: r.sap.poDate || '',
    sPrice: r.sap.unitPrice,
    dateOff: r.diffs.includes('date'),
    priceOff: r.diffs.includes('price'),
    noItem: r.diffs.includes('noItemLine'),
    /* The funnel's values: read and compared only here, so they are written in the reader's language. */
    flags: [r.diffs.includes('date') ? t('xShipDoc.flagDate') : '', r.diffs.includes('price') ? t('xShipDoc.flagPrice') : '',
      r.diffs.includes('noItemLine') ? t('xShipDoc.flagNoItem') : ''].filter(Boolean).join(', '),
  }));

  return (
    <div className="ssd-step">
      {/* ONE ROW (Max, 2026-10-20: "chưa clean và lean lắm"). The line above the tabs (how much was
          read), the tick box, its count and the breakdown on the far right were four more things on
          the screen before the table; the disagreements are a tab now, beside the other three lists,
          and what each tab is and how much was read ride on the hovers. */}
      <div className="ssd-src-tabs">
        {[
          ['in', t('xShipDoc.tabIn', { n: n0(data.rows.length) }),
            t(data.cached ? 'xShipDoc.tabInHintCached' : 'xShipDoc.tabInHint', {
              m: n0(s.masterRows), l: n0(s.poLines), p: n0(s.pos),
              min: data.cached ? Math.max(1, Math.round((Date.now() - data.readAt) / 60000)) : 0,
            })],
          ['diff', t('xShipDoc.tabDiff', { n: n0(s.withDifference) }),
            t(s.priceOnlyInSap ? 'xShipDoc.tabDiffHintSap' : 'xShipDoc.tabDiffHint', {
              d: n0(s.dateDiff), p: n0(s.priceDiff), i: n0(s.noItemLine), s: n0(s.priceOnlyInSap),
            })],
          ['out', t('xShipDoc.tabOut', { n: n0(data.excluded.length) }), t('xShipDoc.tabOutHint')],
          ['unused', t('xShipDoc.tabUnused', { n: n0(data.unused.length) }), t('xShipDoc.tabUnusedHint')],
        ].map(([k, label, hint]) => (
          <button key={k} type="button" className={tab === k ? 'on' : ''} onClick={() => setTab(k)} title={hint}>
            {label}
          </button>
        ))}
      </div>

      {(tab === 'in' || tab === 'diff') && (
        <FilterTable
          key={tab}
          rows={flatRows}
          empty={tab === 'diff' ? t('xShipDoc.diffEmpty') : undefined}
          bands={[{ span: 4, label: '' }, { span: 4, label: 'Master Sheet', cls: 'ssd-src-m' },
            { span: 5, label: 'SAP', cls: 'ssd-src-s' }, { span: 1, label: '' }]}
          getRowKey={(r) => r.rowKey}
          getRowClass={(r) => (r.dateOff || r.priceOff ? 'off' : '')}
          columns={[
            { key: 'destination', label: t('xShipDoc.colDestination'), w: 150 },
            { key: 'customerPo', label: t('xShipDoc.colCustomerPo'), w: 180 },
            { key: 'sfpCode', label: t('xShipDoc.colSfpCode'), w: 92, cls: () => 'mono' },
            { key: 'description', label: t('xShipDoc.colDescription'), w: 210, cls: () => 'ssd-src-desc' },
            { key: 'mQty', label: t('xShipDoc.colQty'), w: 76, num: true },
            { key: 'mExf', label: t('xShipDoc.colExfDate'), w: 100 },
            { key: 'mPrice', label: t('xShipDoc.colUnitPrice'), w: 80, num: true,
              render: (r) => (r.mPrice ? r.mPrice.toFixed(2) : '—') },
            { key: 'mHnxPo', label: 'HNX PO', w: 110, cls: () => 'mono' },
            { key: 'sSfpPo', label: t('xShipDoc.colSfpPo'), w: 110, cls: () => 'mono' },
            { key: 'sSeq', label: t('xShipDoc.colSeq'), w: 62, cls: () => 'mono' },
            { key: 'sPoQty', label: t('xShipDoc.colPoQty'), w: 80, num: true },
            { key: 'sDelivery', label: t('xShipDoc.colDelivery'), w: 100, cls: (r) => (r.dateOff ? 'ssd-gap' : '') },
            { key: 'sPrice', label: t('xShipDoc.colUnitPrice'), w: 80, num: true, cls: (r) => (r.priceOff ? 'ssd-gap' : ''),
              render: (r) => (r.sPrice === null ? '—' : r.sPrice.toFixed(2)) },
            { key: 'flags', label: t('xShipDoc.colDiffers'), w: 140,
              render: (r) => (
                <span className="ssd-cnotes">
                  {r.dateOff && <span className="ssd-chip bad">{t('xShipDoc.flagDate')}</span>}
                  {r.priceOff && <span className="ssd-chip bad">{t('xShipDoc.flagPrice')}</span>}
                  {r.noItem && <span className="ssd-chip bad">{t('xShipDoc.flagNoItem')}</span>}
                  {!r.dateOff && !r.priceOff && !r.noItem && <span className="ssd-src-ok">·</span>}
                </span>
              ) },
          ]}
        />
      )}

      {tab === 'out' && (
        <FilterTable
          rows={data.excluded}
          getRowKey={(r) => r.rowKey}
          empty={t('xShipDoc.outEmpty')}
          columns={[
            { key: 'rowKey', label: t('xShipDoc.colRow'), w: 120, cls: () => 'mono' },
            { key: 'destination', label: t('xShipDoc.colDestination'), w: 150 },
            { key: 'customerPo', label: t('xShipDoc.colCustomerPo'), w: 180 },
            { key: 'sapCode', label: t('xShipDoc.colSapCode'), w: 100, cls: () => 'mono' },
            { key: 'description', label: t('xShipDoc.colDescription'), w: 240, cls: () => 'ssd-src-desc' },
            { key: 'qty', label: t('xShipDoc.colQty'), w: 80, num: true },
            { key: 'exfDate', label: t('xShipDoc.colExfDate'), w: 100 },
            { key: 'reason', label: t('xShipDoc.colWhyOut'), w: 300,
              render: (r) => <span className="ssd-chip bad">{r.reason}</span> },
          ]}
        />
      )}

      {tab === 'unused' && (
        data.unused.length ? (
          <FilterTable
            rows={data.unused}
            getRowKey={(r) => `${r.po}|${r.seq}`}
            columns={[
              { key: 'po', label: t('xShipDoc.colSfpPo'), w: 120, cls: () => 'mono' },
              { key: 'seq', label: t('xShipDoc.colSeq'), w: 70, cls: () => 'mono' },
              { key: 'code', label: t('xShipDoc.colMaterial'), w: 110, cls: () => 'mono' },
              { key: 'qty', label: t('xShipDoc.colQty'), w: 90, num: true },
              { key: 'date', label: t('xShipDoc.colDelivery'), w: 110 },
              { key: 'supplier', label: t('xShipDoc.colSupplier'), w: 220, cls: () => 'ssd-sup' },
            ]}
          />
        ) : (
          <div className="ssd-step-done">
            <Check size={16} />
            {t('xShipDoc.unusedNone')}
          </div>
        )
      )}
    </div>
  );
}

/**
 * DESTINATION — the one column the document prints in a language not all of its readers have.
 * Max, 2026-09-21: "cần 1 tab để dịch destination tiếng hàn thành tiếng anh và xác định giá trị mới
 * sẽ apply cho nó". Eight of the 46 distinct values are Korean and they carry 664 of the rows.
 *
 * A MAPPING BESIDE THE VALUE, NOT AN EDIT OF IT. `destination` comes off the master sheet and every
 * sync rewrites it, so anything typed over the row itself survives until the next run. The English
 * name is stored against the Korean text and applied when the row is drawn and when the file is
 * written — both through `shipDocRows` on the server, so the screen and the export cannot disagree.
 *
 * The list is EVERY destination the document holds, not every row of the alias table: the question
 * is what will be printed, and a translation of a value that has stopped arriving is not that.
 * Korean first, then by row count — the order is the work queue. Nothing is guessed: a destination
 * with no English name prints its own words, which is what it did before this tab existed.
 *
 * SHARED WITH THE MASTER SHEET since 2026-10-03 (Max: "trên Master Sheet, cột Destination sẽ hiển
 * thị tên đã map"). `api` is the only thing that differs — the Master Sheet offers the 116 values
 * ITS rows hold against this document's 46 — and both write the same table, so this is ONE screen
 * rendered twice rather than two screens drifting apart. On that side the folding matters more than
 * the translating: of its 108 unmapped values only seven are Korean, the rest being one place under
 * several spellings (Helinox USA / HUSA, Sea to Summit / STS).
 */
export function DestinationStep({ canEdit, onApplied, api = API, scopeLabel }) {
  const { t } = useTranslation();
  // Which list this is, inside the sentence below — the Master Sheet passes its own, already translated.
  const scope = scopeLabel || t('xShipDoc.scopeDocument');
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState({});     // source -> {display, note}, only while being typed
  const [saving, setSaving] = useState('');
  const [saved, setSaved] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const res = await fetch(`${api}/destinations`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xShipDoc.failed', { status: res.status }));
      setRows(d.destinations || []);
    } catch (e) { setErr(e.message); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  /* The draft holds only what has been typed. Reading the box from the draft with the stored value
     as a fallback means a reload does not have to rebuild it, and an untouched row cannot be saved
     back over itself with a stale value. */
  const valOf = (r, k) => (draft[r.source]?.[k] !== undefined ? draft[r.source][k] : (r[k] || ''));
  const dirty = (r) => draft[r.source] !== undefined
    && (valOf(r, 'display') !== (r.display || '') || valOf(r, 'note') !== (r.note || ''));

  const save = async (r) => {
    setSaving(r.source); setErr('');
    try {
      const res = await fetch(`${api}/destinations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: r.source, display: valOf(r, 'display'), note: valOf(r, 'note') }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || t('xShipDoc.failed', { status: res.status }));
      setDraft((p) => { const n = { ...p }; delete n[r.source]; return n; });
      setSaved(r.source);
      // The tick clears itself; a tick that stays on for ever stops meaning "that one just went".
      setTimeout(() => setSaved((s) => (s === r.source ? '' : s)), 3000);
      await load();
      /* And the DOCUMENT is re-read. The alias is applied on the server as the rows are loaded, so
         without this the tab says the name is saved while the tab beside it still prints the Korean
         — which is exactly what the check caught. */
      if (onApplied) onApplied();
    } catch (e) { setErr(e.message); }
    setSaving('');
  };

  if (err && !rows) return <div className="sp-error"><AlertTriangle size={14} /> {err}</div>;
  if (!rows) return <div className="ssd-loading"><Loader size={14} /> {t('xShipDoc.destLoading')}</div>;

  const korean = rows.filter((r) => r.korean);
  const untranslated = korean.filter((r) => !r.display);

  return (
    <div className="ssd-step">
      <div className="ssd-src-meta">
        <span>
          <Trans t={t} i18nKey="xShipDoc.destHeld" values={{ n: rows.length, scope }} components={{ b: <strong /> }} />
        </span>
        <span className={korean.length ? 'warn' : ''}>
          <Trans t={t} i18nKey="xShipDoc.destKorean" components={{ b: <strong /> }}
            values={{ n: korean.length, rows: korean.reduce((a, r) => a + r.rows, 0) }} />
        </span>
        <span className={untranslated.length ? 'warn' : ''}>
          <Trans t={t} i18nKey="xShipDoc.destNoEnglish" values={{ n: untranslated.length }} components={{ b: <strong /> }} />
        </span>
      </div>

      <p className="ssd-note">
        {t('xShipDoc.destNote')}
      </p>
      {err && <div className="sp-error"><AlertTriangle size={14} /> {err}</div>}

      <table className="ssd-dest-table">
        <thead>
          <tr>
            <th>{t('xShipDoc.destThSource')}</th>
            <th className="num">{t('xShipDoc.colRows')}</th>
            <th>{t('xShipDoc.destThPrinted')}</th>
            <th>{t('xShipDoc.destThNote')}</th>
            <th aria-label={t('xShipDoc.save')} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.source} className={r.korean ? 'is-kr' : ''}>
              <td>
                <span className="ssd-dest-src">{r.source}</span>
                {r.korean && <em className="ssd-dest-kr">KR</em>}
              </td>
              <td className="num">{r.rows}</td>
              <td>
                <input
                  value={valOf(r, 'display')}
                  disabled={!canEdit}
                  placeholder={r.source}
                  onChange={(e) => setDraft((p) => ({
                    ...p, [r.source]: { ...(p[r.source] || {}), display: e.target.value },
                  }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && dirty(r)) save(r); }}
                />
              </td>
              <td>
                <input
                  value={valOf(r, 'note')}
                  disabled={!canEdit}
                  placeholder={t('xShipDoc.destNotePh')}
                  onChange={(e) => setDraft((p) => ({
                    ...p, [r.source]: { ...(p[r.source] || {}), note: e.target.value },
                  }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && dirty(r)) save(r); }}
                />
              </td>
              <td className="ssd-dest-act">
                {/* Three states, the same as the home page's threshold box: quiet with nothing to
                    save, live when there is, a tick when it went — the tick because a colour change
                    on its own is not a state everybody can read. */}
                {saved === r.source ? <span className="ssd-dest-ok"><Check size={13} /> {t('xShipDoc.savedLower')}</span>
                  : (dirty(r) && canEdit ? (
                    <button type="button" className="sp-btn cap-on" disabled={saving === r.source}
                      onClick={() => save(r)}>
                      {saving === r.source ? t('xShipDoc.savingLower') : t('xShipDoc.save')}
                    </button>
                  ) : (r.updatedBy && r.display
                    ? <span className="ssd-dest-by">{r.updatedBy}</span> : null))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
